import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, FileText, Users } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { POLICY_BG_PRIVACY, POLICY_BG_TERMS, POLICY_BG_COMMUNITY } from "@/constants/assets";
import { useLanguage } from "@/hooks/use-language";

// Policy Page - Bento Layout
const PolicyPage = () => {
  const { t } = useLanguage();

  const policies = [
    {
      id: "privacy",
      titleKey: "policy.privacyPolicy",
      updatedKey: "policy.policyDate",
      descriptionKey: "policy.privacyDesc",
      icon: <Shield className="w-8 h-8" />,
      bgImage: POLICY_BG_PRIVACY,
      link: "/policy/privacy",
    },
    {
      id: "terms",
      titleKey: "policy.termsOfService",
      updatedKey: "policy.policyDate",
      descriptionKey: "policy.termsDesc",
      icon: <FileText className="w-8 h-8" />,
      bgImage: POLICY_BG_TERMS,
      link: "/policy/terms",
    },
    {
      id: "community-standards",
      titleKey: "policy.communityStandards",
      updatedKey: "policy.policyDate",
      descriptionKey: "policy.communityDesc",
      icon: <Users className="w-8 h-8" />,
      bgImage: POLICY_BG_COMMUNITY,
      link: "/policy/community-standards",
    },
  ];

  return (
    <div className="bg-[#030014] min-h-screen overflow-x-hidden">
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF2E63] transition-colors mb-8"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              {t("nav.backToHome")}
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-[#FF2E63] text-xs tracking-[0.4em] uppercase mb-4">
              {t("policy.subtitle")}
            </p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white mb-4">
              {t("policy.title1")}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400">
                {t("policy.title2")}
              </span>
            </h1>
            <p className="text-white/50 max-w-lg mx-auto">
              {t("policy.description")}
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policies.map((policy, index) => (
              <motion.div
                key={policy.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              >
                <Link
                  to={policy.link}
                  className="group block relative overflow-hidden rounded-3xl h-[280px] border border-white/5 hover:border-[#FF2E63]/30 transition-all duration-500"
                  data-testid={`policy-${policy.id}`}
                >
                  {/* Background Image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20 transition-opacity duration-500"
                    style={{ backgroundImage: `url(${policy.bgImage})` }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/40" />

                  {/* Content */}
                  <div className="relative z-10 h-full p-8 flex flex-col justify-end">
                    <div className="text-[#FF2E63] mb-4 opacity-80 group-hover:opacity-100 transition-opacity">
                      {policy.icon}
                    </div>
                    <h2 className="font-display font-bold text-2xl text-white mb-2 group-hover:text-[#FF2E63] transition-colors">
                      {t(policy.titleKey)}
                    </h2>
                    <p className="text-white/50 text-sm mb-3">
                      {t(policy.descriptionKey)}
                    </p>
                    <p className="text-white/30 text-xs">
                      {t("policy.updated")}: {t(policy.updatedKey)}
                    </p>
                  </div>

                  {/* Hover Arrow */}
                  <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowLeft className="w-5 h-5 text-white rotate-180" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PolicyPage;
